import { useDraggable } from '@dnd-kit/core'
import { CheckCircle2, GripVertical, Plus } from 'lucide-react'
import { Button } from '@/shared/components/atoms/ui/button'
import { useWorkflowBuilderState } from './WorkflowBuilderState'

/**
 * Step-type palette for the visual workflow builder.
 *
 * Each palette item is both **draggable** (drag onto the canvas) AND
 * supports a one-click **add** via the `+` button — same end-result,
 * different gesture. Drag is the discoverable affordance once the admin
 * gets used to the canvas; the `+` is the always-works fallback when
 * the drag isn't landing (e.g. small screens, hesitant pointer).
 *
 * Drag wiring uses `@dnd-kit/core`'s `useDraggable`. Listeners attach
 * to a small drag-handle (`⋮⋮` icon) rather than the whole card, so
 * the rest of the card stays click-friendly without interfering with
 * pointer-down → drag-start activation.
 */
export function NodePalette({ idPrefix = 'workflow-palette' }: { idPrefix?: string }) {
	const { insertStep, selectStep } = useWorkflowBuilderState()
	const handleAdd = (stepType: PaletteStepType) => {
		const id = insertStep(stepType)
		selectStep(id)
	}
	return (
		<div className="space-y-2" role="region" aria-label="Workflow step palette">
			<p className="text-xs text-muted-foreground">Drag onto the canvas, or click +.</p>
			<ul className="space-y-2" role="list">
				{PALETTE_ITEMS.map((item) => (
					<PaletteItem key={item.stepType} idPrefix={idPrefix} entry={item} onAdd={() => handleAdd(item.stepType)} />
				))}
			</ul>
		</div>
	)
}

// ─── Single palette item ─────────────────────────────────────────────────────

function PaletteItem({ idPrefix, entry, onAdd }: { idPrefix: string; entry: PaletteEntry; onAdd: () => void }) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `${idPrefix}-${entry.stepType}`,
		data: { source: 'palette', stepType: entry.stepType },
	})
	const Icon = entry.icon

	return (
		<li>
			<div
				ref={setNodeRef}
				className={
					'rounded-md border bg-background p-3 text-sm transition-colors hover:border-accent ' +
					(isDragging ? 'opacity-50' : '')
				}
				data-step-type={entry.stepType}
			>
				<div className="flex items-start gap-2">
					{/* Dedicated drag handle. Cursor + listeners + a11y label live
					    here so the card body stays click-friendly. */}
					<button
						type="button"
						className="flex shrink-0 cursor-grab items-center text-muted-foreground hover:text-foreground active:cursor-grabbing"
						aria-label={`Drag to add ${entry.label}`}
						{...listeners}
						{...attributes}
					>
						<GripVertical className="size-4" />
					</button>

					<Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />

					<div className="flex-1 space-y-2">
						<div className="flex items-start justify-between gap-2">
							<p className="font-semibold">{entry.label}</p>
							<Button
								type="button"
								size="sm"
								variant="outline"
								className="h-7 shrink-0 px-2"
								onClick={onAdd}
								aria-label={`Add ${entry.label} step to canvas`}
							>
								<Plus className="size-3.5" />
								Add
							</Button>
						</div>
						<p className="text-xs text-muted-foreground">{entry.summary}</p>
						<div className="space-y-1">
							<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
								What you'll fill in
							</p>
							<ul className="ml-4 list-disc space-y-0.5 text-xs text-muted-foreground">
								{entry.requiredInputs.map((line) => (
									<li key={line}>{line}</li>
								))}
							</ul>
						</div>
					</div>
				</div>
			</div>
		</li>
	)
}

// ─── Palette items table ─────────────────────────────────────────────────────

// Palette-only step type. The schema discriminator (`@kfolks/types`'s
// `WorkflowStepType`) also includes `'info_gate'`, but we don't surface
// info-gate as a palette item — info-request is now an approver-side
// action ("Request more info" on the approval inbox), not an authored
// step. The schema keeps `info_gate` for legacy reads from JSONB.
type PaletteStepType = 'approval'

interface PaletteEntry {
	stepType: PaletteStepType
	label: string
	/** One-line, plain-language description of the step's purpose. */
	summary: string
	/**
	 * Bullet list of the inputs the admin will need to provide on the
	 * properties panel after dropping. Phrased as user-facing fields, not
	 * schema field names. Limit to the inputs an admin must care about
	 * (skip technical/optional fields like `skipIfSubmitterIn`).
	 */
	requiredInputs: ReadonlyArray<string>
	icon: React.ComponentType<{ className?: string }>
}

const PALETTE_ITEMS: ReadonlyArray<PaletteEntry> = [
	{
		stepType: 'approval',
		label: 'Approval',
		summary: 'Wait for someone to approve or perform an action.',
		requiredInputs: [
			'A name (e.g. "Manager review")',
			'Who can act (people, group, or manager)',
			'Optional: SLA hours',
		],
		icon: CheckCircle2,
	},
]

type _ExhaustiveCheck = Record<PaletteStepType, true>
const _ASSERT_EXHAUSTIVE: _ExhaustiveCheck = Object.fromEntries(
	PALETTE_ITEMS.map((item) => [item.stepType, true]),
) as _ExhaustiveCheck
void _ASSERT_EXHAUSTIVE
